import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Sparkles, Check, Zap, ChevronRight, Rocket, ShieldCheck, HeartHandshake, Users, Building2, Sprout, Home as HomeIcon, Bell } from "lucide-react";
import { APPS, LIVE_APPS, ROADMAP_APPS, cleanName } from "@/apps";
import AppDetail from "@/AppDetail";
import Footer from "@/Footer";
import NavBar from "@/NavBar";
import { Privacy, Terms, Support, DeleteAccount } from "@/Legal";
import { BetaSignupDialog, NewsletterSection, LaunchListDialog } from "@/Forms";
import { StoreBadges } from "@/StoreBadges";
import AccessibilityWidget from "@/Accessibility";
import Roadmap from "@/Roadmap";
import Security from "@/Security";
import Compare from "@/Compare";
import NotFound from "@/NotFound";
import Admin from "@/Admin";
import { Toaster } from "sonner";

const Card = ({ app, index, waiting = 0 }) => {
  const WAITLIST_GOAL = 100;
  const isLive = app.live && app.url;
  const features = (app.features || []).slice(0, 3);
  const navigate = useNavigate();
  const go = () => navigate(`/${app.slug}`);

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="h-full"
    >
      <div
        role="link"
        tabIndex={0}
        onClick={go}
        onKeyDown={(e) => { if (e.key === "Enter") go(); }}
        data-testid={`app-card-${app.id}`}
        className="hi-card group relative block h-full cursor-pointer"
      >
        <div className="p-6 md:p-7 flex flex-col h-full">
          <div className="absolute top-4 right-4 z-[3]">
            {isLive ? (
              <span data-testid={`badge-${app.id}`} className="hi-body inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#7fe0a0] bg-[#1f7a45]/25 border border-[#39c274]/40 rounded-full px-2.5 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#4ade80] animate-pulse" />
                {app.beta ? "Live · Beta" : "Live"}
              </span>
            ) : waiting >= WAITLIST_GOAL ? (
              <span data-testid={`badge-${app.id}`} className="hi-body inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#1a2417] bg-[#c9a24b] rounded-full px-2.5 py-1 shadow-[0_0_18px_-4px_rgba(201,162,75,0.8)]">
                <Sparkles size={10} strokeWidth={2.6} /> Launching soon!
              </span>
            ) : (
              <span data-testid={`badge-${app.id}`} className="hi-body inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#8fbaff] bg-[#3b9bff]/12 border border-[#3b9bff]/30 rounded-full px-2.5 py-1">
                <Lock size={10} strokeWidth={2.6} /> Soon
              </span>
            )}
          </div>

          <div className="flex flex-col items-center text-center">
            <div className="hi-logo-glow mb-5">
              <img src={app.logo} alt={`${app.name} logo`} loading="lazy" className="w-24 h-24 md:w-28 md:h-28 rounded-2xl object-cover" />
            </div>
            <h3 className="hi-serif text-2xl tracking-wide text-[#f3ead4]">{app.name}</h3>
            <p className="hi-body text-[11px] uppercase tracking-[0.18em] mt-1.5" style={{ color: app.accent }}>{app.tagline}</p>
            {!isLive && app.launch && (
              <span data-testid={`card-launch-${app.id}`} className="hi-body inline-flex items-center gap-1.5 mt-2 text-[10px] font-bold uppercase tracking-wider text-[#e0bb63] bg-[#c9a24b]/12 border border-[#c9a24b]/30 rounded-full px-2.5 py-0.5">
                Target · {app.launch}
              </span>
            )}
          </div>

          <ul data-testid={`features-${app.id}`} className="mt-5 space-y-3 flex-1">
            {features.map((f) => (
              <li key={f.name} className="flex items-start gap-2.5 text-left">
                <span className={`mt-0.5 shrink-0 grid place-items-center w-5 h-5 rounded-full ${f.pro ? "bg-[#c9a24b]/20 text-[#e0bb63]" : "bg-white/10 text-white/70"}`}>
                  {f.pro ? <Zap size={12} strokeWidth={2.6} /> : <Check size={12} strokeWidth={3} />}
                </span>
                <span className="hi-body text-sm leading-snug">
                  <span className="font-semibold text-[#f3ead4]">{f.name}</span>
                  {f.pro && <span className="ml-2 align-middle text-[9px] font-bold tracking-wider uppercase text-[#1a2417] bg-[#c9a24b] rounded px-1.5 py-[1px]">Pro</span>}
                  <span className="block text-white/70 text-[13px] mt-0.5">{f.desc}</span>
                </span>
              </li>
            ))}
          </ul>

          {isLive && (
            <div className="mt-5 flex justify-center">
              <StoreBadges app={app} />
            </div>
          )}

          <div className="flex flex-col items-center gap-3">
            <div data-testid={`view-details-${app.id}`} className="hi-cta mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold hi-body bg-[#c9a24b] text-[#1a2417] group-hover:bg-[#e0bb63]">
              View Details <ChevronRight size={16} strokeWidth={2.5} />
            </div>
            {!isLive && (
              <div onClick={(e) => e.stopPropagation()} className="flex flex-col items-center gap-2 w-full">
                {waiting > 0 && (
                  <div className="w-full max-w-[220px]" data-testid={`waitlist-progress-${app.id}`}>
                    <div className="flex items-center justify-between text-[11px] text-[#8fbaff] mb-1">
                      <span className="inline-flex items-center gap-1.5"><Users size={12} /> {waiting} waiting</span>
                      <span className="text-white/45">Goal {WAITLIST_GOAL}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#1f5fbf] to-[#3b9bff] transition-all duration-700"
                        style={{ width: `${Math.min(100, Math.round((waiting / WAITLIST_GOAL) * 100))}%` }}
                      />
                    </div>
                  </div>
                )}
                <LaunchListDialog
                  defaultApp={cleanName(app.name)}
                  trigger={
                    <button
                      data-testid={`launch-list-${app.id}`}
                      className="hi-cta inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold hi-body bg-white/5 border border-[#3b9bff]/40 text-[#8fbaff] hover:bg-[#3b9bff]/15"
                    >
                      <Bell size={15} strokeWidth={2.5} /> Join the Launch List
                    </button>
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const SectionHeading = ({ eyebrow, title, subtitle, testid }) => (
  <div className="mb-8" data-testid={testid}>
    <div className="hi-hairline w-16 mb-4" />
    <p className="hi-body text-xs uppercase tracking-[0.3em] text-[#3b9bff]">{eyebrow}</p>
    <h2 className="hi-serif text-3xl md:text-4xl text-[#f3ead4] mt-2">{title}</h2>
    {subtitle && <p className="hi-body text-white/70 mt-2 max-w-2xl">{subtitle}</p>}
  </div>
);

const WhySection = () => {
  const items = [
    { icon: HeartHandshake, title: "Built for real life", body: "We build tools we use ourselves — for the farm, the business and the family. Practical first, no filler." },
    { icon: ShieldCheck, title: "Private by default", body: "Your data is yours. We don't sell it, and you can delete it anytime. Simple as that." },
    { icon: Sparkles, title: "Thoughtful AI", body: "AI that helps with the hard parts — identifying a pest, reading a receipt — without getting in your way." },
  ];
  return (
    <section className="max-w-6xl mx-auto px-5 py-16" data-testid="why-section">
      <SectionHeading eyebrow="Our approach" title="Why Herrell Interactive?" subtitle="Understated, dependable software from a family that runs a homestead and small businesses — made for people who keep things running." />
      <div className="grid md:grid-cols-3 gap-5">
        {items.map((it, i) => (
          <motion.div key={it.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="hi-card p-6">
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#3b9bff]/15 text-[#3b9bff] border border-[#3b9bff]/25 mb-4"><it.icon size={20} /></span>
            <h3 className="hi-serif text-xl text-[#f3ead4]">{it.title}</h3>
            <p className="hi-body text-sm text-white/70 mt-2 leading-relaxed">{it.body}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const PrivacyTeaser = () => (
  <section className="max-w-6xl mx-auto px-5 pb-4" data-testid="privacy-teaser">
    <div className="hi-card p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6">
      <span className="grid place-items-center w-14 h-14 rounded-2xl bg-[#3b9bff]/15 text-[#3b9bff] border border-[#3b9bff]/25 shrink-0"><ShieldCheck size={26} /></span>
      <div className="flex-1">
        <h3 className="hi-serif text-2xl text-[#f3ead4]">Privacy & Security, in plain language</h3>
        <p className="hi-body text-white/70 mt-2 max-w-2xl">Your data stays yours. We don't sell your information, we protect it with strong encryption, and you can leave anytime.</p>
      </div>
      <Link to="/security" data-testid="privacy-teaser-link" className="hi-cta inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold hi-body bg-white/5 border border-white/15 text-white/85 hover:bg-white/10 shrink-0">
        Read our approach <ChevronRight size={16} strokeWidth={2.5} />
      </Link>
    </div>
  </section>
);

const BreadthBanner = () => {
  const items = [
    { icon: Users, label: "Families" },
    { icon: Building2, label: "Small Businesses" },
    { icon: Sprout, label: "Agriculture" },
    { icon: HomeIcon, label: "Everyday Life" },
  ];
  return (
    <section className="max-w-6xl mx-auto px-5 py-10" data-testid="breadth-banner">
      <div className="hi-card px-6 py-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
        {items.map((it) => (
          <div key={it.label} className="flex items-center gap-2.5 text-white/80">
            <it.icon size={18} className="text-[#c9a24b]" />
            <span className="hi-body text-sm font-semibold tracking-wide">{it.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

const Home = () => {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/launch-list/counts`)
      .then((r) => (r.ok ? r.json() : {}))
      .then(setCounts)
      .catch(() => {});
  }, []);
  return (
    <div className="hi-bg hi-grain min-h-screen">
      <NavBar />
      <div className="relative z-[2]">
        {/* Hero */}
        <header className="max-w-6xl mx-auto px-5 pt-10 md:pt-16 pb-4 text-center">
          <h1 className="sr-only" data-testid="hub-title">HERRELL INTERACTIVE — Apps designed for real life.</h1>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} data-testid="hub-banner" className="relative rounded-3xl overflow-hidden ring-1 ring-[#1f5fbf]/40 shadow-[0_30px_80px_-30px_rgba(31,95,191,0.55)]">
            <img src="/logos/hi-banner.png" alt="Herrell Interactive — Apps designed for real life." className="w-full h-auto block" />
          </motion.div>

          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }} data-testid="hero-subtagline" className="hi-cormorant italic text-lg md:text-2xl text-white/80 mt-7 max-w-3xl mx-auto">
            Practical software for business, home, family and the people who keep them running.
          </motion.p>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45, duration: 0.6 }} className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <span data-testid="hub-tagline" className="hi-body inline-flex items-center gap-2 text-xs text-white/70 bg-white/5 border border-white/10 rounded-full px-4 py-1.5">
              <Sparkles size={14} className="text-[#3b9bff]" />
              {APPS.length} apps · {LIVE_APPS.length} live now
            </span>
            <BetaSignupDialog trigger={
              <button data-testid="join-beta-btn" className="hi-cta inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold hi-body bg-gradient-to-r from-[#1f5fbf] to-[#3b9bff] text-white hover:brightness-110">
                <Rocket size={16} strokeWidth={2.5} /> Join the Beta
              </button>
            } />
          </motion.div>
        </header>

        {/* Live Now */}
        <main className="max-w-6xl mx-auto px-5 pt-12 md:pt-16">
          <SectionHeading eyebrow="Available now" title="Live Now" subtitle="In active beta and ready to use today." testid="section-live" />
          <div data-testid="grid-live" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {LIVE_APPS.map((app, i) => <Card key={app.id} app={app} index={i} />)}
          </div>

          {/* On the Roadmap */}
          <div className="mt-20">
            <SectionHeading eyebrow="Coming soon" title="On the Roadmap" subtitle="In design and development. Pre-register with “Join the Launch List” and we'll email you the moment it's ready." testid="section-roadmap" />
            <div data-testid="grid-roadmap" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {ROADMAP_APPS.map((app, i) => <Card key={app.id} app={app} index={i} waiting={counts[cleanName(app.name)] || 0} />)}
            </div>
          </div>
        </main>

        <WhySection />
        <PrivacyTeaser />
        <NewsletterSection />
        <BreadthBanner />
        <Footer />
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" theme="dark" richColors closeButton />
      <AccessibilityWidget />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/roadmap" element={<Roadmap />} />
          <Route path="/security" element={<Security />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/support" element={<Support />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/:slug" element={<AppDetail />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
